// ── Password strength — pure, framework-free (unit-testable) ─────────────────
// A CLIENT-SIDE UX hint only. The real rule is enforced server-side
// (registerSchema.password.min(8) in packages/api/src/routes/auth.ts). Keep
// MIN_PASSWORD_LENGTH in sync with that.

export const MIN_PASSWORD_LENGTH = 8;

export type StrengthLevel = "empty" | "weak" | "fair" | "good" | "strong";

export interface Strength {
  score: number;        // 0–4, drives the 4-segment meter
  level: StrengthLevel;
  label: string;        // human label, "" when empty
}

/** Meets the server's minimum length. Client gate mirrors the server's real check. */
export function meetsMinimum(pw: string): boolean {
  return pw.length >= MIN_PASSWORD_LENGTH;
}

/** Do two entries match AND are non-empty (used for confirm-password). */
export function passwordsMatch(a: string, b: string): boolean {
  return a.length > 0 && a === b;
}

/**
 * A 0–4 strength score. Under the min length it is always "weak" (Too short) so
 * the meter never rewards a short-but-complex password the server will reject.
 */
export function passwordStrength(pw: string): Strength {
  if (!pw)                 return { score: 0, level: "empty", label: "" };
  if (!meetsMinimum(pw))   return { score: 1, level: "weak",  label: "Too short" };

  let score = 1;                                   // length >= min
  if (pw.length >= 12)                     score++;
  if (/[a-z]/.test(pw) && /[A-Z]/.test(pw)) score++;
  if (/\d/.test(pw))                       score++;
  if (/[^A-Za-z0-9]/.test(pw))             score++;

  const clamped = Math.min(4, score);
  const level: StrengthLevel =
    clamped <= 1 ? "weak" : clamped === 2 ? "fair" : clamped === 3 ? "good" : "strong";
  const label = { weak: "Weak", fair: "Fair", good: "Good", strong: "Strong" }[level];
  return { score: clamped, level, label };
}
